import { useMutation } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { problemDetail } from '@up2d8/api-client';
import type { MediaPurpose } from '@up2d8/contracts';
import { useUpload } from '../../shared/use-upload';
import { UploadBox } from './upload-box';

/** Wat de logo's aanvaarden; de server saneert een SVG (MRK-3). Het app-icoon blijft PNG/WebP. */
export const LOGO_ACCEPT = 'image/png,image/webp,image/svg+xml';
export const BITMAP_ACCEPT = 'image/png,image/webp';
export const BACKGROUND_ACCEPT = 'image/png,image/webp,image/jpeg';

/**
 * Eén afbeelding van een huisstijl of van het platformmerk: uploaden, bekijken, weghalen.
 *
 * Altijd via `useUpload` en de media-module (MRK-0): plek vragen, bytes naar de opslag, afronden,
 * en dan pas het bestand aan zijn plek hangen met `attach`. Het verschil tussen de afbeeldingen zit
 * in de regel die de server erover heeft - en die hoort niet in de client herhaald te worden. Wat
 * de server weigert, komt hier als melding terug.
 */
export function BrandImageField({
  kind,
  label,
  hint,
  url,
  hasImage = Boolean(url),
  fallback,
  dark,
  accept,
  purpose,
  attach,
  onSaved,
}: {
  /** Voor de testids: `branding-<kind>`, `remove-<kind>`, `image-<kind>`. */
  kind: string;
  label: string;
  hint: ReactNode;
  url: string | null;
  /** Of er op dít niveau een afbeelding staat; een geërfde toont wel, maar is niet weg te halen. */
  hasImage?: boolean;
  fallback: ReactNode;
  dark?: boolean;
  accept: string;
  purpose: MediaPurpose;
  attach: (fileId: string | null) => Promise<void>;
  onSaved: (message: string) => void;
}) {
  const { t } = useTranslation();
  const upload = useUpload();

  const bewaar = useMutation({
    mutationFn: attach,
    onSuccess: (_result, fileId) => {
      onSaved(fileId ? t('branding.imageSaved') : t('branding.imageRemoved'));
    },
  });

  const onSelect = async (file: File): Promise<void> => {
    // Zonder `tenantId`: een bedrijfsbeheerder heeft zijn bedrijf in de sessie, en voor het
    // platform kiest de media-module zelf de vaste platformtenant (ADR-0084).
    const fileId = await upload.upload(file, { ownerModule: 'tenants', purpose });
    if (fileId) bewaar.mutate(fileId);
  };

  const fout =
    upload.error ??
    (bewaar.isError ? (problemDetail(bewaar.error) ?? t('media.uploadFailed')) : null);

  return (
    <UploadBox
      testId={`branding-${kind}`}
      removeTestId={`remove-${kind}`}
      label={label}
      hint={hint}
      dark={dark}
      accept={accept}
      hasImage={hasImage}
      preview={url ? <img src={url} alt={label} data-testid={`image-${kind}`} /> : fallback}
      progress={upload.progress}
      error={fout}
      busy={bewaar.isPending}
      onSelect={(file) => void onSelect(file)}
      onCancel={upload.cancel}
      onRemove={() => bewaar.mutate(null)}
    />
  );
}
